/**
 * <em-before-after data-direction="horizontal|vertical" data-initial="50">
 * Draggable comparison: the "after" layer is clipped at --em-ba-position. Mouse users can click or drag
 * anywhere; touch users drag the handle (or tap to jump); keyboard users move the handle with arrows.
 */

const TAP_MAX_MS = 250;

class EmBeforeAfter extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.handle = this.querySelector('.em-ba__handle');
    this.vertical = this.dataset.direction === 'vertical';
    this.position = 0;
    this.dragging = false;
    this.tapStartedAt = 0;

    this.onMove = (event) => {
      this.cancelIntro();
      this.setFromPointer(event);
      this.classList.add('is-dragging');
    };

    this.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
    document.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
    document.addEventListener('pointercancel', () => this.stopDragging(), { signal });

    this.handle?.addEventListener(
      'touchstart',
      (event) => {
        event.preventDefault();
        this.startDragging();
      },
      { signal, passive: false }
    );

    this.handle?.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.playIntro();
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.stopDragging();
  }

  get initial() {
    const value = parseFloat(this.dataset.initial || '50');
    return Number.isFinite(value) ? value : 50;
  }

  set(percent) {
    this.position = Math.min(100, Math.max(0, percent));
    this.style.setProperty('--em-ba-position', `${this.position}%`);
    this.handle?.setAttribute('aria-valuenow', String(Math.round(this.position)));
  }

  setFromPointer(event) {
    const box = this.getBoundingClientRect();
    let percent;

    if (this.vertical) {
      percent = ((event.clientY - box.top) / box.height) * 100;
    } else {
      percent = ((event.clientX - box.left) / box.width) * 100;
      if (getComputedStyle(this).direction === 'rtl') percent = 100 - percent;
    }

    this.set(percent);
  }

  onPointerDown(event) {
    if (event.target.closest('a')) return;

    if (event.pointerType === 'mouse' || window.matchMedia('(hover: hover)').matches) {
      this.cancelIntro();
      this.setFromPointer(event);
      this.startDragging();
    } else if (!this.handle?.contains(event.target)) {
      this.tapStartedAt = event.timeStamp;
    }
  }

  onPointerUp(event) {
    const wasDragging = this.dragging;
    this.stopDragging();

    if (!wasDragging && this.tapStartedAt && event.timeStamp - this.tapStartedAt <= TAP_MAX_MS && this.contains(event.target)) {
      this.cancelIntro();
      this.setFromPointer(event);
    }

    this.tapStartedAt = 0;
  }

  startDragging() {
    if (this.dragging) return;
    this.dragging = true;
    document.addEventListener('pointermove', this.onMove);
  }

  stopDragging() {
    this.dragging = false;
    document.removeEventListener('pointermove', this.onMove);
    this.classList.remove('is-dragging');
  }

  onKeydown(event) {
    const keys = this.vertical ? ['ArrowUp', 'ArrowDown'] : ['ArrowLeft', 'ArrowRight'];
    if (!keys.includes(event.key)) return;

    event.preventDefault();
    this.cancelIntro();
    const step = event.key === keys[0] ? -1 : 1;
    this.set(this.position + step);
  }

  playIntro() {
    const target = this.initial;
    const canAnimate =
      window.CSS?.registerProperty !== undefined && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!canAnimate) {
      this.set(target);
      return;
    }

    this.intro = this.animate(
      [{ '--em-ba-position': `${this.position}%` }, { '--em-ba-position': `${target}%` }],
      { duration: 600, easing: 'cubic-bezier(0.85, 0, 0.15, 1)', fill: 'forwards' }
    );

    this.intro.finished
      .then(() => {
        this.set(target);
        this.cancelIntro();
      })
      .catch(() => {});
  }

  cancelIntro() {
    if (!this.intro) return;
    const intro = this.intro;
    this.intro = null;
    intro.cancel();
  }
}

if (!customElements.get('em-before-after')) {
  customElements.define('em-before-after', EmBeforeAfter);
}
